import { useBookingFlow } from "@/hooks/useBookingFlow";
import { TimeSlot } from "@/types/booking";

/**
 * Shared logic for DateStep components
 * Provides common utilities and state getters from bookingFlowStore
 */
export const useDateStepLogic = () => {
  const {
    selectedDate,
    selectedTimeSlots,
    firstAvailableDate,
    defaultSecondSlotDate,
    secondSlotCalendarDate,
    selectDate,
    selectTimeSlot,
    deleteTimeSlot,
    setSecondSlotCalendarDate,
  } = useBookingFlow();

  // Utility to check if two dates are the same day
  const isSameDay = (dateA: Date, dateB: Date): boolean => {
    return (
      dateA.getDate() === dateB.getDate() &&
      dateA.getMonth() === dateB.getMonth() &&
      dateA.getFullYear() === dateB.getFullYear()
    );
  };

  // Utility to check if a time slot exists for a given date
  const hasTimeSlotForDate = (date: Date): boolean => {
    return selectedTimeSlots.some((slot) => isSameDay(slot.date, date));
  };

  // Create a function to check if a date is disabled
  // Optionally exclude a specific slot index
  const createIsDateDisabled =
    (excludeSlotIndex?: number) =>
    (date: Date): boolean => {
      return selectedTimeSlots.some((slot, index) => {
        if (excludeSlotIndex !== undefined && index === excludeSlotIndex) {
          return false;
        }
        return isSameDay(slot.date, date);
      });
    };

  return {
    // State from store
    selectedDate,
    selectedTimeSlots,
    firstAvailableDate,
    defaultSecondSlotDate,
    secondSlotCalendarDate,

    // Actions from store
    selectDate,
    selectTimeSlot,
    deleteTimeSlot,
    setSecondSlotCalendarDate,

    // Utilities
    isSameDay,
    hasTimeSlotForDate,
    createIsDateDisabled,
  };
};
